/**
 * CategoriesStore — المخزن المركزي للتصنيفات
 * يقرأ ويكتب من Supabase مباشرة
 */

import { supabase } from "@/lib/supabase";
import { queryCache } from "@/lib/queryCache";

export interface Category {
  id: number;
  name: string;
  nameEn?: string;
  slug: string;
  icon: string;
  count: number;
  parentSlug?: string;
  type?: string;
  country?: string;
  countryEn?: string;
  image?: string;
}

function mapRow(row: Record<string, unknown>): Category {
  return {
    id: row.id as number,
    name: row.name as string,
    nameEn: (row.name_en as string | undefined) || undefined,
    slug: row.slug as string,
    icon: row.icon as string,
    count: row.count as number,
    parentSlug: (row.parent_slug as string) || undefined,
    type: (row.type as string) || undefined,
    country: (row.country as string) || undefined,
    countryEn: (row.country_en as string | undefined) || undefined,
    image: (row.image as string) || undefined,
  };
}

function dispatch() {
  queryCache.invalidate("categories:");
  window.dispatchEvent(new CustomEvent("alsiyahiah:categories:updated"));
}

export const categoriesStore = {
  async getAll(): Promise<Category[]> {
    const cached = queryCache.get<Category[]>("categories:all", 120_000); // 2 min
    if (cached) return cached;

    const { data, error } = await supabase
      .from("categories")
      .select("*")
      .order("id", { ascending: true });
    if (error) {
      console.error("categoriesStore.getAll error:", error);
      return [];
    }
    const result = (data || []).map(mapRow);
    queryCache.set("categories:all", result);
    return result;
  },

  async getByParent(parentSlug: string): Promise<Category[]> {
    const cacheKey = `categories:parent:${parentSlug}`;
    const cached = queryCache.get<Category[]>(cacheKey, 120_000);
    if (cached) return cached;

    const { data, error } = await supabase
      .from("categories")
      .select("*")
      .eq("parent_slug", parentSlug)
      .order("count", { ascending: false });
    if (error) {
      console.error("categoriesStore.getByParent error:", error);
      return [];
    }
    const result = (data || []).map(mapRow);
    queryCache.set(cacheKey, result);
    return result;
  },

  async getCities(): Promise<Category[]> {
    const cached = queryCache.get<Category[]>("categories:cities", 120_000);
    if (cached) return cached;

    const { data, error } = await supabase
      .from("categories")
      .select("*")
      .eq("type", "city")
      .order("count", { ascending: false });
    if (error) {
      console.error("categoriesStore.getCities error:", error);
      return [];
    }
    const result = (data || []).map(mapRow);
    queryCache.set("categories:cities", result);
    return result;
  },

  async add(cat: Category): Promise<void> {
    const { data: existing } = await supabase
      .from("categories")
      .select("id")
      .eq("slug", cat.slug)
      .maybeSingle();
    if (existing) return;
    const { error } = await supabase.from("categories").insert({
      name: cat.name,
      name_en: cat.nameEn || null,
      slug: cat.slug,
      icon: cat.icon,
      count: cat.count,
    });
    if (error) {
      console.error("categoriesStore.add error:", error);
      return;
    }
    dispatch();
  },

  async update(slug: string, data: Partial<Category>): Promise<void> {
    const db: Record<string, unknown> = {};
    if (data.name !== undefined) db.name = data.name;
    if (data.nameEn !== undefined) db.name_en = data.nameEn || null;
    if (data.country !== undefined) db.country = data.country;
    if (data.countryEn !== undefined) db.country_en = data.countryEn || null;
    if (data.icon !== undefined) db.icon = data.icon;
    if (data.count !== undefined) db.count = data.count;
    const { error } = await supabase
      .from("categories")
      .update(db)
      .eq("slug", slug);
    if (error) {
      console.error("categoriesStore.update error:", error);
      return;
    }
    dispatch();
  },

  async delete(slug: string): Promise<void> {
    const { error } = await supabase.from("categories").delete().eq("slug", slug);
    if (error) {
      console.error("categoriesStore.delete error:", error);
      return;
    }
    dispatch();
  },

  async updateCount(slug: string, count: number): Promise<void> {
    const { error } = await supabase
      .from("categories")
      .update({ count })
      .eq("slug", slug);
    if (error) {
      console.error("categoriesStore.updateCount error:", error);
      return;
    }
    dispatch();
  },

  async reset(): Promise<void> {
    dispatch();
  },
};